import { DateRangeFilterYear } from 'lib/apiClient';
import { Locale } from 'lib/config/config';

/**
 * Takes applied search startDate range
 * and formats to be user readable
 * @param {string} dateRange e.g. ['2022-03-03~2022-04-20']
 * @return {string | undefined} User readable format e.g. 'March 3 - April 20 2022'
 */

type DateTimeFormatOptions = {
	month: 'long' | 'short' | 'narrow';
	day: 'numeric' | '2-digit';
	year?: 'numeric' | '2-digit';
};

export interface MonthsGrouping {
	year: string;
	monthGroups: MonthGroup[];
}

export interface MonthGroup {
	code: string[];
	label: string[];
	startRange: string;
	endRange: string;
	dateRanges: string[];
}

export const getDateRangeLabel = (dateRange: string, locale: Locale): string | undefined => {
	const [start, end] = dateRange.split('~');

	if (!start || !end) {
		return undefined;
	}

	const [sYear, sMonth, sDay] = start.split('-').map((str) => Number(str));
	const [eYear, eMonth, eDay] = end.split('-').map((str) => Number(str));

	if (!sYear || !eYear || !sMonth || !eMonth || !sDay || !eDay) {
		return undefined;
	}

	const showStartYear = sYear !== eYear;

	const startDate = new Date(sYear, sMonth - 1, sDay);
	const startFormat: DateTimeFormatOptions = showStartYear ? { month: 'long', day: 'numeric', year: 'numeric' } : { month: 'long', day: 'numeric' };
	const startDateFormatted = startDate.toLocaleString(locale.language, startFormat).replace(/,/g, '');

	const endDate = new Date(eYear, eMonth - 1, eDay);
	const endDateFormatted = endDate.toLocaleString(locale.language, { month: 'long', day: 'numeric', year: 'numeric' }).replace(/,/g, '');

	return `${startDateFormatted} - ${endDateFormatted}`;
};

/**
 *
 * @param dateRangeFilter
 *
 * @returns - Array of months gruped.
 * Each monthGroup is defined by the consecutive months that are selected within a year.
 *  [{
 *    year: '2024',
 *    monthGroups: [{
 *      code: ['2024-01', '2024-02'],
 *      label: ['Jan', 'Feb'],
 *      startRange: '2024-01-01',
 *      endRange: '2024-02-29',
 *      dateRanges: ['2024-01-01~2024-01-31', '2024-02-01~2024-02-29']
 *    }]
 *  }]
 */
export function getMonthGroupsByYear(years: Array<DateRangeFilterYear>): Array<MonthsGrouping> {
	const monthsGrouping: Array<MonthsGrouping> = [];

	years.forEach((year) => {
		let newGroup = true;

		const groups: MonthGroup[] = [];

		year.months.forEach((month) => {
			if (month.isSelected) {
				if (newGroup) {
					groups.push({
						code: [month.code],
						label: [month.label],
						startRange: month.dateRange.split('~')[0],
						endRange: month.dateRange.split('~')[1],
						dateRanges: [month.dateRange],
					});
				} else {
					const lastGroup = groups[groups.length - 1];
					groups[groups.length - 1].code = [...lastGroup.code, month.code];
					groups[groups.length - 1].label = [...lastGroup.label, month.label];
					groups[groups.length - 1].startRange = lastGroup.dateRanges[0].split('~')[0];
					groups[groups.length - 1].endRange = month.dateRange.split('~')[1];
					groups[groups.length - 1].dateRanges = [...lastGroup.dateRanges, month.dateRange];
				}

				newGroup = false;

				return;
			}

			newGroup = true;
		});

		monthsGrouping.push({
			year: year.code,
			monthGroups: groups,
		});
	});

	return monthsGrouping;
}
